// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { ExcelImport } from '@client/banks/components/shared/excel-import';
import { getBankUploadExcelLimitValues } from '@client/banks/excel/bank-upload-excel-limits';
import { NavigationControls } from '@client/banks/layouts/navigation-controls';
import { ScreenLayout } from '@client/banks/layouts/screen-layout';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useAccountsImport } from './use-accounts-import';

const excelLimits = getBankUploadExcelLimitValues();

export const AccountsImport = defineComponent({
  displayName: 'AccountsImport',
  useSetup: useAccountsImport,
  View: props => (
    <ScreenLayout
      actions={
        <NavigationControls
          hideNext
          onPrevious={props.onBackToList}
          previousLabel={<Translation i18nKey="common.go-back" ns="common" />}
          previousStep="accounts-list"
        />
      }
    >
      <ExcelImport
        data-testid="bank-upload-accounts-import"
        title={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.title"
            ns="aggregator"
          />
        }
        description={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.description"
            ns="aggregator"
          />
        }
        stepDownloadTitle={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.step-download-title"
            ns="aggregator"
          />
        }
        stepDownloadDescription={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.step-download-description"
            ns="aggregator"
          />
        }
        stepUploadTitle={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.step-upload-title"
            ns="aggregator"
          />
        }
        stepUploadDescription={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.step-upload-description"
            ns="aggregator"
          />
        }
        downloadTemplateLabel={
          <Translation
            i18nKey="aggregator.create-bank.excel.download-template"
            ns="aggregator"
          />
        }
        dragHereLabel={
          <Translation
            i18nKey="aggregator.create-bank.excel.drag-here"
            ns="aggregator"
          />
        }
        fileConstraintsLabel={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.file-constraints"
            ns="aggregator"
            values={excelLimits}
          />
        }
        selectFileLabel={
          <Translation
            i18nKey="aggregator.create-bank.excel.select-files"
            ns="aggregator"
          />
        }
        processingNote={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.processing-note"
            ns="aggregator"
          />
        }
        submitLabel={
          <Translation
            i18nKey="aggregator.create-bank.accounts.import.submit"
            ns="aggregator"
          />
        }
        fileInputRef={props.fileInputRef}
        selectedFile={props.selectedFile}
        isDragging={props.isDragging}
        excelAccept={props.excelAccept}
        hasSelectedFile={props.hasSelectedFile}
        isParsing={props.isParsing}
        parseErrors={props.parseErrors}
        onDownloadTemplate={props.onDownloadTemplate}
        onSelectFileClick={props.onSelectFileClick}
        onFileInputChange={props.onFileInputChange}
        onDragOver={props.onDragOver}
        onDragLeave={props.onDragLeave}
        onDrop={props.onDrop}
        onRemoveFile={props.onRemoveFile}
        onImport={props.onImport}
      />
    </ScreenLayout>
  ),
});
